/**
 * What a loading picture says, worked out from the clock (roadmap D85).
 *
 * A picture that waits on the host — the branch map, Architecture, the
 * timeline, a plan's graph, every repository's memory — shows what it is
 * waiting for instead of an empty frame. After a few seconds it also says
 * how long it has waited, and once that is clearly too long it offers to
 * ask again: an answer that never comes (a host that died, a message that
 * was dropped) must not leave a frozen area with no way out. A failure the
 * view knows about is shown as a failure, with the reason and a Retry.
 *
 * Kept free of React and the DOM so the webview tests can hold it.
 */

/** Seconds of waiting before the time waited is shown at all: a quick answer should not flash a counter. */
export const SHOW_ELAPSED_AFTER_MS = 3_000;
/** Waiting this long is no longer normal: the picture says so and offers to ask again. */
export const SLOW_AFTER_MS = 20_000;

export type LoadingPhase = 'loading' | 'slow' | 'failed';

export interface LoadingLook {
  phase: LoadingPhase;
  /** "12 s", "1 min 5 s" — only once the wait is long enough to be worth saying. */
  elapsed?: string;
}

/** A wait in words a person reads at a glance. */
export function elapsedWords(ms: number): string {
  const seconds = Math.max(0, Math.floor(ms / 1000));
  if (seconds < 60) return `${seconds} s`;
  const minutes = Math.floor(seconds / 60);
  const rest = seconds % 60;
  return rest ? `${minutes} min ${rest} s` : `${minutes} min`;
}

export function loadingLook(facts: {
  startedAt: number;
  now: number;
  /** What went wrong, when the view knows. Any non-empty text is a failure. */
  error?: string | null;
  showElapsedAfterMs?: number;
  slowAfterMs?: number;
}): LoadingLook {
  if (facts.error) return { phase: 'failed' };
  const waited = Math.max(0, facts.now - facts.startedAt);
  const showAfter = facts.showElapsedAfterMs ?? SHOW_ELAPSED_AFTER_MS;
  const slowAfter = facts.slowAfterMs ?? SLOW_AFTER_MS;
  const elapsed = waited >= showAfter ? elapsedWords(waited) : undefined;
  return { phase: waited >= slowAfter ? 'slow' : 'loading', ...(elapsed ? { elapsed } : {}) };
}
